import { useEffect, type ReactNode } from 'react';

import { Icon } from './Icon.js';

/** A single tab shown across the top of a wide editor dialog. */
export interface ModalTab {
  id: string;
  label: string;
}

/**
 * Dialog width presets. The PSA UI runs on large desktop monitors, so entity
 * editors default to a wide page-like panel rather than a slim phone-width modal.
 * `sm` is reserved for confirmations and other tiny prompts.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'wide';

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  wide: 'max-w-6xl',
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  /** Panel width. Entity editors should use `wide`; confirmations use `sm`. */
  size?: ModalSize;
  /** When provided, renders a tab bar across the top of the dialog. */
  tabs?: ModalTab[];
  /** Id of the currently active tab (required when `tabs` is set). */
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  /**
   * Pin the panel to a constant height (max available) instead of sizing to its
   * content. Use for tabbed editors so the tab bar stays in a fixed position as
   * the user switches between tabs with differing amounts of content.
   */
  fillHeight?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  headerActions,
  size = 'md',
  tabs,
  activeTabId,
  onTabChange,
  fillHeight = false,
}: ModalProps): JSX.Element | null {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const hasTabs = Boolean(tabs && tabs.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative z-10 flex w-full ${SIZE_CLASS[size]} flex-col rounded-xl bg-white shadow-xl ${
          fillHeight ? 'h-[calc(100vh-3rem)]' : 'max-h-[calc(100vh-3rem)]'
        }`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <div className="flex items-center gap-2">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <Icon name="close" className="h-5 w-5" />
            </button>
          </div>
        </header>
        {hasTabs ? (
          <nav className="flex gap-1 border-b border-slate-200 px-6" aria-label="Sections">
            {tabs?.map((tab) => {
              const active = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange?.(tab.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium ${
                    active
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        ) : null}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
